import type { MouseEvent as ReactMouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { KibButtonNew } from '@chewy/kib-controls-react'
import type { CheckboxProps } from '@mui/material'
import { LanguageUtils } from '@pbt/pbt-ui-components'

import {
  useIsDrug,
  useIsFood,
  useIsGlobalInventoryItem,
  useIsVetDiet,
} from '~/store/hooks/orders'
import { getOrderIsCreating } from '~/store/reducers/orders'
import type { Order, OrderCallbackWithTypeCheck } from '~/types'
import {
  CheckboxColor,
  dropOrderPrepaidBox,
  getIsRefill,
} from '~/utils/orderUtils'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import styles from './RhapReminderOrderListItem.module.scss'

const RhapReminderOrderListItem = ({
  order,
  isCheckedItem,
  onCheckItem,
  onUncheckItem,
  getItemColor,
  onCreated: onCreatedProp,
  isDisabled,
  ListItemProps = {},
}: {
  ListItemProps?: {
    onRefillSelect?: (prescription: Order) => void
    showFoodListItemActions?: boolean
    showListItemActions?: boolean
  }
  getItemColor?: (order: Order) => CheckboxProps['color']
  isCheckedItem: (item: Order) => boolean
  isDisabled?: boolean
  onCheckItem: OrderCallbackWithTypeCheck
  onCreated?: OrderCallbackWithTypeCheck
  onUncheckItem: OrderCallbackWithTypeCheck
  order: Order
}) => {
  const { t } = useTranslation(['Common'])
  const isGlobalItem = useIsGlobalInventoryItem(order)
  const isFood = useIsFood(order)
  const isDrug = useIsDrug(order)
  const isVetDiet = useIsVetDiet(order)
  const isRefill = getIsRefill(order)

  const { onRefillSelect } = ListItemProps

  const { prepaidBox } = order
  const orderName = LanguageUtils.getTranslatedFieldName(order)
  const remainsHint =
    prepaidBox &&
    `(${t('Common:QUANTITY_REMAINS', { quantity: prepaidBox.remaining })})`
  const displayName = [orderName, remainsHint].filter(Boolean).join(' ')
  const color =
    typeof getItemColor === 'function'
      ? getItemColor(order)
      : CheckboxColor.PRIMARY

  const isIntermediateState = color !== CheckboxColor.PRIMARY

  const isPrescribable =
    isGlobalItem || (isFood && order.inventory?.requiresPrescription)

  const isCheckBoxChecked = isCheckedItem(order)

  const onCreated = (checkedOrder: Order) => {
    onCreatedProp?.(checkedOrder, { isPrescribable, isDrug, isFood })
  }

  const setProceedOnOrderCreated = useCloseAfterCreation(
    onCreated,
    getOrderIsCreating,
  )

  const handleSelectionChange = () => {
    if (isCheckBoxChecked) {
      onUncheckItem(order, { isPrescribable, isDrug, isVetDiet, isFood })
    } else {
      const checkedOrder = isIntermediateState
        ? dropOrderPrepaidBox(order)
        : order

      setProceedOnOrderCreated(checkedOrder)
      onCheckItem(checkedOrder, {
        isPrescribable,
        isDrug,
        isVetDiet,
        isFood,
      })
    }
  }

  const handleClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()

    if (isDisabled) {
      return
    }

    if (isRefill) {
      if (onRefillSelect) {
        onRefillSelect(order)
      }
    } else {
      handleSelectionChange() // In this case we change the status of checkbox
    }
  }

  return (
    <div className={styles.container}>
      <span>{displayName}</span>
      <KibButtonNew
        className={styles.ctaButton}
        disabled={isDisabled}
        emphasis={isCheckedItem(order) ? 'secondary' : 'primary'}
        size="small"
        onClick={handleClick}
      >
        {isCheckedItem(order)
          ? t('Common:REMOVE_ACTION')
          : t('Common:ADD_ACTION')}
      </KibButtonNew>
    </div>
  )
}

export default RhapReminderOrderListItem
